import { atom, RecoilState, selector, selectorFamily } from 'recoil';
import { AnnotationRecord } from '~/domain/annotation/annotation';
import {
    DistanceMeasurement,
    Measurement,
    MeasurementDraft,
    MeasurementKind,
} from '~/domain/measurement/measurement';
import type { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    AnnotationListState,
    annotationListState,
} from '~/state/atoms/annotationListAtoms';
import {
    intensityRegionDraftState,
    intensityRegionListState,
    lineProfileDraftState,
    lineProfileListState,
} from '~/state/atoms/intensityToolAtoms';
import { KEEP_LATEST_ONLY } from '~/state/atoms/selectorCachePolicy';
import { MeasureFamily } from '~/utils/annotationKinds';

/** Recoil state for the measurement tool; the record model lives in the domain. */

/** The committed measurements, one atom each, oldest first. */
export const measurementListState =
    annotationListState<Measurement>('measurement');

/** The current in-progress placement, or null when idle. */
export const measurementDraftState = atom<MeasurementDraft | null>({
    key: 'measurementDraftState',
    default: null,
});

/** The rulers of a structure's long and short diameter, the long one first. */
export interface MeasuredAxialDiameters
    extends Pick<RoiReadModel, 'roiId' | 'structureSetUid'> {
    readonly rulers: readonly DistanceMeasurement[];
}

/**
 * The measured diameters, one pair per structure, in the order measured. They
 * stay on the image whichever structure is selected, and are drawn there only,
 * never in {@link measurementListState}.
 */
export const measuredAxialDiametersState = atom<
    readonly MeasuredAxialDiameters[]
>({
    key: 'measuredAxialDiametersState',
    default: [],
});

/** Every measured diameter's ruler, pair after pair. */
const measuredAxialDiameterRulersState = selector<
    readonly DistanceMeasurement[]
>({
    key: 'measuredAxialDiameterRulersState',
    get: ({ get }) =>
        get(measuredAxialDiametersState).flatMap(({ rulers }) => rulers),
    cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
});

/** The measured diameters' ruler `id`, or undefined; read like a list's item. */
export const measuredAxialDiameterState = selectorFamily<
    Measurement | undefined,
    string
>({
    key: 'measuredAxialDiameterState',
    get:
        (id) =>
        ({ get }) =>
            get(measuredAxialDiameterRulersState).find(
                (ruler) => ruler.id === id
            ),
    cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
});

/** Which kind of measurement the next placement creates. */
export const measurementModeState = atom<MeasurementKind>({
    key: 'measurementModeState',
    default: 'distance',
});

/** The family last measured with, which Measure resumes. A UI preference. */
export const measureFamilyState = atom<MeasureFamily>({
    key: 'measureFamilyState',
    default: 'size',
});

/** Whether the Measure tray stays open while the pointer is elsewhere. */
export const measureTrayPinnedState = atom<boolean>({
    key: 'measureTrayPinnedState',
    default: false,
});

/** The lists Measure places into; RecoilState is invariant, hence the cast. */
export const MEASURE_ANNOTATION_LISTS = [
    measurementListState,
    lineProfileListState,
    intensityRegionListState,
] as unknown as readonly AnnotationListState<AnnotationRecord>[];

/** The drafts Measure places through; RecoilState is invariant, hence the cast. */
export const MEASURE_DRAFT_STATES = [
    measurementDraftState,
    lineProfileDraftState,
    intensityRegionDraftState,
] as unknown as readonly RecoilState<unknown>[];

/** Shapes placed, across all kinds, the measured diameters' rulers included. */
export const placedAnnotationCountState = selector<number>({
    key: 'placedAnnotationCountState',
    get: ({ get }) =>
        MEASURE_ANNOTATION_LISTS.flatMap((list) => get(list.ids)).length +
        get(measuredAxialDiameterRulersState).length,
});

/** Distinct slices (series, orientation, plane) those shapes lie on. */
export const placedAnnotationSliceCountState = selector<number>({
    key: 'placedAnnotationSliceCountState',
    get: ({ get }) =>
        new Set(
            [
                ...MEASURE_ANNOTATION_LISTS.flatMap((list) => get(list.items)),
                ...get(measuredAxialDiameterRulersState),
            ].map(({ plane }) =>
                [
                    plane.seriesInstanceUid,
                    plane.orientation,
                    ...plane.viewDirection,
                    plane.planeCoordinateMm,
                ].join('|')
            )
        ).size,
    cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
});

/** True while a shape of any kind is being placed. */
export const annotationPlacementActiveState = selector<boolean>({
    key: 'annotationPlacementActiveState',
    get: ({ get }) => MEASURE_DRAFT_STATES.some((draft) => get(draft) !== null),
    cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
});
